import { ChangeDetectionStrategy, Component, EventEmitter, Output, ViewChild } from "@angular/core";
import { NotificationsFacade } from "@modules/notifications/domain/+state/notifications.facade";
import { Observable } from "rxjs";
import { Overlay, OverlayRef, ScrollStrategy } from "@angular/cdk/overlay";
import { NotificationsBoardComponent } from "@modules/notifications/features/notifications-board/notifications-board.component";
import { ComponentPortal } from "@angular/cdk/portal";
import { BreakpointObserver } from "@angular/cdk/layout";
import { UntilDestroy, untilDestroyed } from "@ngneat/until-destroy";
import { NotificationsCountModel } from "@modules/notifications/domain/entities/notifications.model";
import { filter } from "rxjs/operators";
import { BREAK_POINT_SCREEN_MD } from "@helpers/constants";

@UntilDestroy()
@Component({
	selector: "notification-bell",
	templateUrl: "./notification-bell.component.html",
	styleUrls: ["./notification-bell.component.scss"],
	changeDetection: ChangeDetectionStrategy.OnPush,
	standalone: false
})
export class NotificationBellComponent {
	@Output()
	public isOverlayOpen: EventEmitter<boolean> = new EventEmitter<boolean>();
	@ViewChild("bell") bell;
	public maxMessageCount: number = 99;
	public countModel$: Observable<NotificationsCountModel> = this.notificationsFacade
		.getState$()
		.pipe(filter((value) => value !== null));
	private overlayRef: OverlayRef = null;

	public constructor(
		private readonly notificationsFacade: NotificationsFacade,
		private readonly overlay: Overlay,
		private readonly responsive: BreakpointObserver
	) {}

	private breakpointChanged(): void {
		// this value is added to fit with our current breakpoints, otherwise by default for mobile screens BreakpointObserver has a breakpoint at 600px
		if (this.responsive.isMatched(BREAK_POINT_SCREEN_MD)) {
			this.overlayRef = this.createOverlay();
		} else {
			this.overlayRef = this.createSmallScreenOverlay();
		}
	}

	public openModal(): void {
		this.responsive
			.observe([BREAK_POINT_SCREEN_MD])
			.pipe(untilDestroyed(this))
			.subscribe(() => {
				this.breakpointChanged();
			});

		const compRef = this.overlayRef.attach(new ComponentPortal(NotificationsBoardComponent));

		compRef.instance.onCloseClick.pipe(untilDestroyed(this)).subscribe(() => {
			this.notificationsFacade.handleDialogClose();
			this.isOverlayOpen.emit(false);
			this.overlayRef.dispose();
		});

		this.overlayRef
			.backdropClick()
			.pipe(untilDestroyed(this))
			.subscribe((_) => {
				this.notificationsFacade.handleDialogClose();
				this.isOverlayOpen.emit(false);
				this.overlayRef.dispose();
			});

		this.isOverlayOpen.emit(false);
		this.notificationsFacade.rudderstackTracking("Bell Icon Clicked");
	}

	private createSmallScreenOverlay(): OverlayRef {
		const positionStrategy = this.overlay.position().global().centerHorizontally().top("60px");
		const scrollStrategy: ScrollStrategy = this.overlay.scrollStrategies.close({ threshold: 30 });

		return this.overlay.create({
			width: "100%",
			height: "auto",
			hasBackdrop: true,
			backdropClass: "cdk-overlay-transparent-backdrop",
			panelClass: ["panel-animation"],
			positionStrategy,
			scrollStrategy
		});
	}

	private createOverlay(): OverlayRef {
		const positionStrategy = this.overlay
			.position()
			.flexibleConnectedTo(this.bell)
			.withPositions([
				{
					originX: "end",
					originY: "bottom",
					overlayX: "end",
					overlayY: "top"
				}
			]);
		const scrollStrategy: ScrollStrategy = this.overlay.scrollStrategies.close({ threshold: 250 });

		return this.overlay.create({
			height: "auto",
			minWidth: 300,
			hasBackdrop: true,
			backdropClass: "cdk-overlay-transparent-backdrop",
			panelClass: ["panel-animation"],
			positionStrategy,
			scrollStrategy
		});
	}
}
